import { useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Body1,
  Button,
  Label,
  Select,
} from "@fluentui/react-components";
import { Delete16Regular, Edit16Regular } from "@fluentui/react-icons";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "@fluentui/react-components/unstable";
import AdminNavbar from "../../../components/layouts/AdminNavbar";
import PageTitle from "../../../components/common/PageTitle";
import { CenteredLayout } from "../../../components/layouts";
import ResourceAllocationOrderDialog from "../../../components/layouts/ResourceAllocationOrderDialog";
import ReadOnlyInfoBanner from "../../../components/common/ReadOnlyInfoBanner";
import { AppConfirmdialog, AppLoader } from "../../../components/common";
import useResourceAllocationOrders from "../../../hooks/useResourceAllocationOrders";
import dateFormatService from "../../../services/dateFormat.service";
import { monthNames } from "../../../utils/consts";
import { formatResourceAllocationDuration } from "../../../utils/resourceAllocationDuration";
import type { IResourceAllocationOrderApi } from "../../../services/types/resourceAllocation";
import styles from "../../../components/layouts/EmplyeesListStyles";

type RouteParams = {
  employeeId: string;
};

function parseQuery(search: string) {
  const params = new URLSearchParams(search);
  const year = parseInt(params.get("year") ?? "", 10);
  const month = parseInt(params.get("month") ?? "", 10);
  const employeeIdFilter = params.get("employeeId") ?? "";
  return {
    year: Number.isFinite(year) ? year : new Date().getFullYear(),
    month: Number.isFinite(month) && month >= 1 && month <= 12 ? month : new Date().getMonth() + 1,
    employeeIdFilter,
  };
}

type LocationState = {
  employeeName?: string;
};

const ResourceAllocationEmployeeDetail = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { employeeId } = useParams<RouteParams>();
  const { year, month, employeeIdFilter } = useMemo(() => parseQuery(location.search), [location.search]);
  const selectedMonthLabel = monthNames[month - 1] ?? monthNames[0];

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [openOrderDialog, setOpenOrderDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [editingOrder, setEditingOrder] = useState<IResourceAllocationOrderApi | null>(null);
  const [orderToDelete, setOrderToDelete] = useState<IResourceAllocationOrderApi | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const {
    orders,
    projects,
    isLoading,
    isSubmitting,
    readOnlyMode,
    createOrder,
    updateOrder,
    deleteOrder,
  } = useResourceAllocationOrders(employeeId, year, month, selectedProjectId || undefined);

  const employeeName =
    location.state?.employeeName ??
    (orders[0] ? `${orders[0].lastName} ${orders[0].firstName}` : "");

  const pageTitle = employeeName
    ? `Zlecenia: ${employeeName}`
    : "Zlecenia godzin pracy";

  const goBack = () => {
    const employeeQuery = employeeIdFilter ? `&employeeId=${encodeURIComponent(employeeIdFilter)}` : "";
    history.push(`/resource-allocation?year=${year}&month=${month}${employeeQuery}`);
  };

  const openAddDialog = () => {
    setDialogMode("add");
    setEditingOrder(null);
    setOpenOrderDialog(true);
  };

  const openEditDialog = (order: IResourceAllocationOrderApi) => {
    setDialogMode("edit");
    setEditingOrder(order);
    setOpenOrderDialog(true);
  };

  const openDeleteConfirm = (order: IResourceAllocationOrderApi) => {
    setOrderToDelete(order);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!orderToDelete || !deleteOrder) return;
    await deleteOrder(orderToDelete.id);
    setOpenDeleteDialog(false);
    setOrderToDelete(null);
  };

  const isOrderLocked = (order: IResourceAllocationOrderApi) =>
    order.settlementStatus === "accepted";

  return (
    <>
      <AdminNavbar />
      <CenteredLayout>
        <PageTitle title={pageTitle} />
        <div className="table-container" style={styles.resourceAllocationPageContainer}>
          <Card style={styles.tableContainer}>
            <Body1>
              <Button
                appearance="primary"
                onClick={goBack}
                style={styles.resourceAllocationButtonBack}
              >
                Powrót do alokacji zasobów
              </Button>

              <div style={styles.tableWithFiltersHeader}>
                {readOnlyMode ? (
                  <ReadOnlyInfoBanner message="Tryb podglądu — administrator nie może modyfikować zleceń." />
                ) : null}

                <div style={styles.resourceAllocationToolbarRow}>
                  <div style={styles.toolbarFilterGroup}>
                    <Label size="small" htmlFor="resource-allocation-detail-month">
                      Miesiąc
                    </Label>
                    <Select
                      id="resource-allocation-detail-month"
                      style={styles.tableFilter}
                      value={selectedMonthLabel}
                      disabled
                    >
                      <option value={selectedMonthLabel}>{selectedMonthLabel}</option>
                    </Select>
                  </div>
                  <div style={styles.toolbarFilterGroup}>
                    <Label size="small" htmlFor="resource-allocation-detail-year">
                      Rok
                    </Label>
                    <Select
                      id="resource-allocation-detail-year"
                      style={styles.tableFilterYear}
                      value={year}
                      disabled
                    >
                      <option value={year}>{year}</option>
                    </Select>
                  </div>
                  <div style={styles.toolbarFilterGroup}>
                    <Label size="small" htmlFor="resource-allocation-project-filter">
                      Nazwa projektu
                    </Label>
                    <Select
                      id="resource-allocation-project-filter"
                      style={styles.tableFilter}
                      value={selectedProjectId}
                      onChange={(e) => setSelectedProjectId(e.target.value)}
                    >
                      <option value="">Wszystkie</option>
                      {projects.map((project) => (
                        <option key={project.youTrackProjectId} value={project.youTrackProjectId}>
                          {project.projectName}
                        </option>
                      ))}
                    </Select>
                  </div>
                  {!readOnlyMode ? (
                    <div style={styles.resourceAllocationToolbarActions}>
                      <Button
                        appearance="primary"
                        style={styles.addAssignmentButton}
                        onClick={openAddDialog}
                        disabled={isSubmitting || projects.length === 0}
                      >
                        Dodaj zlecenie
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="main-table-container">
                <Table noNativeElements aria-label="Tabela zleceń alokacji zasobów" style={styles.table}>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Nazwa projektu</TableHeaderCell>
                      {readOnlyMode ? <TableHeaderCell>Kierownik</TableHeaderCell> : null}
                      <TableHeaderCell>Nazwa zlecenia</TableHeaderCell>
                      <TableHeaderCell>Zlecone godziny</TableHeaderCell>
                      <TableHeaderCell>Zaakceptowane godziny</TableHeaderCell>
                      <TableHeaderCell>Data i czas zlecenia</TableHeaderCell>
                      {!readOnlyMode ? (
                        <TableHeaderCell style={styles.actionsHeaderCell}>Akcje</TableHeaderCell>
                      ) : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.length === 0 && !isLoading && (
                      <h4 className="table-empty-value">Brak danych</h4>
                    )}
                    {orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>
                          <TableCellLayout>{order.youTrackProjectLabel}</TableCellLayout>
                        </TableCell>
                        {readOnlyMode ? (
                          <TableCell>
                            <TableCellLayout>{order.managerFullName ?? "—"}</TableCellLayout>
                          </TableCell>
                        ) : null}
                        <TableCell>
                          <TableCellLayout>{order.assignmentDescription}</TableCellLayout>
                        </TableCell>
                        <TableCell>
                          <TableCellLayout>
                            {formatResourceAllocationDuration(order.durationMinutes)}
                          </TableCellLayout>
                        </TableCell>
                        <TableCell>
                          <TableCellLayout>
                            {formatResourceAllocationDuration(order.acceptedYouTrackMinutes ?? 0)}
                          </TableCellLayout>
                        </TableCell>
                        <TableCell>
                          <TableCellLayout>
                            {`${dateFormatService.formatDate(order.createTime)} ${dateFormatService.formatDateToTime(order.createTime)}`}
                          </TableCellLayout>
                        </TableCell>
                        {!readOnlyMode ? (
                          <TableCell style={styles.actionsHeaderCell}>
                            <TableCellLayout>
                              <Button
                                size="small"
                                appearance="subtle"
                                icon={<Edit16Regular />}
                                onClick={() => openEditDialog(order)}
                                disabled={isSubmitting || isOrderLocked(order)}
                              >
                                Edytuj
                              </Button>
                              <Button
                                size="small"
                                appearance="subtle"
                                icon={<Delete16Regular />}
                                onClick={() => openDeleteConfirm(order)}
                                disabled={isSubmitting || isOrderLocked(order)}
                              >
                                Usuń
                              </Button>
                            </TableCellLayout>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                    {(isLoading || isSubmitting) && orders.length === 0 && <AppLoader />}
                  </TableBody>
                </Table>
              </div>
            </Body1>
          </Card>
        </div>

        {!readOnlyMode && createOrder && updateOrder ? (
          <ResourceAllocationOrderDialog
            open={openOrderDialog}
            setOpen={setOpenOrderDialog}
            mode={dialogMode}
            selectedMonth={selectedMonthLabel}
            selectedYear={year}
            projects={projects}
            initialProjectId={selectedProjectId || undefined}
            order={editingOrder}
            onSubmitAdd={createOrder}
            onSubmitEdit={(payload) =>
              editingOrder
                ? updateOrder(editingOrder.id, payload)
                : Promise.resolve()
            }
            isSubmitting={isSubmitting}
          />
        ) : null}

        <AppConfirmdialog
          title="Potwierdź usunięcie"
          bodyTxt="Czy na pewno chcesz usunąć to zlecenie?"
          setOpen={setOpenDeleteDialog}
          open={openDeleteDialog}
          onAccept={handleDelete}
          isLoading={isSubmitting}
        />
      </CenteredLayout>
    </>
  );
};

export default ResourceAllocationEmployeeDetail;
